import type { Deal, DealStage, Pipeline } from './types';
import { OUTCOME_LABELS } from './types';

/**
 * The Deals board drawn as columns.
 *
 * ── What Kyu asked for ─────────────────────────────────────────────────────
 *
 * "I need Kanban options here. for both CG and Partnerships." The List view
 * — called Board until this shipped, renamed because cards in columns have a
 * better claim on the word — groups by SECTION: Headline deals, TBD today,
 * Waiting on others, which is how a day gets triaged. It cannot show where the
 * pipeline is bunched. Fifteen of twenty-two CG deals sat in one stage the day
 * this was written and nothing on screen said so.
 *
 * His stages, given 31 Aug 2026 and applied by migration 20261221000100:
 *
 *   CG Deals      In Talks → Deck Sent → Follow Up            → Won or Lost
 *   Partnerships  Meeting Booked → In talks → Follow Up
 *                                → Contract/Nego              → Won or Lost
 *
 * ── Why Won and Lost are not stages ────────────────────────────────────────
 *
 * They are `deals.outcome`, an enum of open/won/lost carrying `closed_on`,
 * written by set_deal_outcome — and `lead_deal_outcomes` derives a lead's Won
 * and Lost positions from it. A stage by the same name would be a SECOND Won,
 * free to disagree with the first: a card dragged to a Won stage while its
 * outcome stayed 'open' would read Won on this board and Converted to Deal on
 * the lead, with nothing on either screen explaining why.
 *
 * So the columns are not one list. `kanbanColumns` returns the stages followed
 * by two OUTCOME columns, and each column says which kind it is. The caller
 * uses that to decide whether a drop writes `stage_id` or calls the outcome
 * mutation — see `dropIntent`, which makes that decision here rather than
 * leaving it to a component to remember.
 *
 * ── The rule about closed deals ────────────────────────────────────────────
 *
 * A closed deal keeps its stage. Won does not erase where the deal got to, and
 * a deal reopened after being lost must land back where it was rather than at
 * the start. So a closed deal is drawn in its OUTCOME column, and the stage
 * columns hold open deals only — otherwise a won deal would appear twice.
 */

/** A column is either a stage somebody set, or an outcome somebody marked. */
export type ColumnKind = 'stage' | 'outcome';

export interface KanbanColumn {
  /** Stage id for a stage column; 'won' or 'lost' for an outcome column. */
  key: string;
  name: string;
  kind: ColumnKind;
  deals: Deal[];
}

/** What a drop onto a column should actually write. */
export type DropIntent =
  | { write: 'stage'; stageId: string }
  | { write: 'outcome'; outcome: 'won' | 'lost' }
  | { write: 'reopen'; stageId: string }
  | { write: 'nothing' };

/**
 * Deals with no stage at all.
 *
 * 20 of 29 partnerships deals had none the day this shipped, and Kyu's answer
 * was "we will do it manually". They are NOT quietly dropped and NOT swept
 * into the first column — either would hide work or assert a position nobody
 * chose. They get a column of their own, first, so the size of the job is
 * visible every time the board is opened.
 */
export const UNSTAGED_KEY = 'unstaged';

export function kanbanColumns(
  deals: readonly Deal[],
  stages: readonly DealStage[],
): KanbanColumn[] {
  const open = deals.filter((deal) => deal.outcome === 'open');
  const byStage = new Map<string, Deal[]>();
  for (const deal of open) {
    if (deal.stage_id === null) continue;
    const held = byStage.get(deal.stage_id);
    if (held === undefined) {
      byStage.set(deal.stage_id, [deal]);
    } else {
      held.push(deal);
    }
  }

  const unstaged = open.filter((deal) => deal.stage_id === null);
  const ordered = [...stages].sort((a, b) => a.position - b.position);

  const columns: KanbanColumn[] = [];

  // Only when it has something in it. A permanently empty "No stage" column on
  // a tidy board is chrome; on an untidy one it is the whole point.
  if (unstaged.length > 0) {
    columns.push({ key: UNSTAGED_KEY, name: 'No stage', kind: 'stage', deals: unstaged });
  }

  for (const stage of ordered) {
    columns.push({
      key: stage.id,
      name: String(stage.name),
      kind: 'stage',
      deals: byStage.get(stage.id) ?? [],
    });
  }

  // Always present, even at zero. An empty Won column is a true and useful
  // statement about this business — nothing had ever been marked Won — and
  // hiding it would have hidden that.
  columns.push({
    key: 'won',
    name: 'Won',
    kind: 'outcome',
    deals: deals.filter((deal) => deal.outcome === 'won'),
  });
  columns.push({
    key: 'lost',
    name: 'Lost',
    kind: 'outcome',
    deals: deals.filter((deal) => deal.outcome === 'lost'),
  });

  return columns;
}

/**
 * What dropping `deal` onto `column` should write.
 *
 * Four cases, and the two that are easy to get wrong are the ones involving a
 * closed deal:
 *
 *   open  → stage      set stage_id
 *   open  → outcome    mark won/lost; the stage is LEFT ALONE so reopening
 *                      returns the deal to where it actually got to
 *   closed → stage     reopen AND set the stage, in that order — a deal
 *                      dragged out of Won is being un-closed, and leaving it
 *                      won while moving its stage is the disagreement this
 *                      whole design exists to prevent
 *   either → same col  nothing
 */
export function dropIntent(deal: Deal, column: KanbanColumn): DropIntent {
  if (column.kind === 'outcome') {
    const wanted = column.key === 'won' ? 'won' : 'lost';
    if (deal.outcome === wanted) {
      return { write: 'nothing' };
    }
    return { write: 'outcome', outcome: wanted };
  }

  const stageId = column.key === UNSTAGED_KEY ? '' : column.key;

  if (deal.outcome !== 'open') {
    return { write: 'reopen', stageId };
  }
  if ((deal.stage_id ?? '') === stageId) {
    return { write: 'nothing' };
  }
  return { write: 'stage', stageId };
}

/**
 * The one-line summary under the board.
 *
 * Counts OPEN deals only, because "13 deals" that silently included closed ones
 * would disagree with the columns a reader can see and count.
 */
export function kanbanSummary(columns: readonly KanbanColumn[]): string {
  const open = columns
    .filter((column) => column.kind === 'stage')
    .reduce((total, column) => total + column.deals.length, 0);
  const unstaged = columns.find((column) => column.key === UNSTAGED_KEY)?.deals.length ?? 0;
  const head = `${open} open deal${open === 1 ? '' : 's'}`;
  return unstaged === 0 ? head : `${head} · ${unstaged} with no stage`;
}

/** Remembered per browser, per pipeline, like every other view toggle here. */
export function viewStorageKey(pipeline: Pipeline): string {
  return `tap.deals.view.${pipeline}`;
}

// ── The dropdown that agrees with the board ────────────────────────────────
//
// Kyu, 8 September, with the list open beside the board: "Deal stage in the
// list view does not reflect the true deals stages in Kanban. Kanban shows the
// real deal stages and it should show in the dropdown for deals in the list
// tab or in the deal profile as well."
//
// He was looking at Hornetsecurity: Deck Sent in the list, Won on the board.
// Both true — stage and outcome are two columns — and the list only showed one
// of them. The board has always merged the two into one "where is it" answer;
// this is the same answer, as a dropdown rather than a drag.
//
// One rule, reused, not a second one: a choice here becomes a DropIntent
// through dropIntent(), exactly as a drop does. So picking Won from the list
// writes what dragging to Won writes, and picking a stage on a won deal
// reopens it the way dragging it out of Won does. The dropdown and the board
// cannot disagree because there is nothing for them to disagree with.

/** Where a deal is, as one value: its outcome if closed, else its stage. */
export function placeOf(deal: Pick<Deal, 'outcome' | 'stage_id'>): string {
  if (deal.outcome !== 'open') return deal.outcome;
  return deal.stage_id ?? '';
}

/**
 * Where a deal is, as words, for a screen that shows it rather than edits it.
 *
 * The same answer placeOf gives a dropdown, resolved to a label. It exists
 * because the All Deals list did not have it and printed the raw stage name
 * instead, so a closed deal showed the stage it was in when it closed.
 *
 * Hanneza, 16 September, on Anatomie: the list said "Meeting Booked" while the
 * deal card said Lost. Both were reading real columns. Nothing was corrupt:
 * stage and outcome are two columns and a lost deal keeps the stage it was
 * lost in, deliberately, because "lost at Meeting Booked" is worth knowing.
 * The list was just answering a different question from every other screen.
 *
 * This is the third time this exact shape has been fixed. Kyu, 8 September, on
 * Hornetsecurity: "Deal stage in the list view does not reflect the true deals
 * stages in Kanban." That fix gave the board and the drawer placeOf, and the
 * All Deals list was not on the list of screens that got it.
 */
export function placeLabel(
  deal: Pick<Deal, 'outcome' | 'stage_id'>,
  stageNames: ReadonlyMap<string, string>,
): string {
  if (deal.outcome !== 'open') {
    return OUTCOME_LABELS[deal.outcome];
  }
  if (deal.stage_id === null) {
    return '\u2014';
  }
  return stageNames.get(deal.stage_id) ?? '\u2014';
}

/** The choices, in the board's order: no stage, the stages, then the two outcomes. */
export function placeOptions(
  stages: readonly { id: string; name: string }[],
): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'No stage' },
    ...stages.map((stage) => ({ value: stage.id, label: stage.name })),
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
  ];
}

/**
 * What choosing `value` should write. The same four cases as a drop, because
 * it IS a drop: the value is turned into the column it names and handed to
 * dropIntent.
 */
export function placeIntent(deal: Deal, value: string): DropIntent {
  const column: KanbanColumn =
    value === 'won' || value === 'lost'
      ? { key: value, name: value, kind: 'outcome', deals: [] }
      : { key: value === '' ? UNSTAGED_KEY : value, name: value, kind: 'stage', deals: [] };
  return dropIntent(deal, column);
}
